import { useState, useEffect } from "react";
import axios from "axios";
import { Clock, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DISMISS_KEY = "shx_countdown_dismissed";

const computeDiff = (targetIso) => {
  const target = new Date(targetIso).getTime();
  const now = Date.now();
  const ms = target - now;
  if (ms <= 0) return { expired: true, d: 0, h: 0, m: 0, s: 0 };
  const d = Math.floor(ms / 86400000);
  const h = Math.floor((ms % 86400000) / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  const s = Math.floor((ms % 60000) / 1000);
  return { expired: false, d, h, m, s };
};

export const CountdownBanner = () => {
  const [settings, setSettings] = useState(null);
  const [time, setTime] = useState(null);
  const [dismissed, setDismissed] = useState(() => {
    return sessionStorage.getItem(DISMISS_KEY) === "1";
  });

  // Fetch settings once
  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/countdown`)
      .then((res) => {
        if (!cancelled) setSettings(res.data);
      })
      .catch(() => {
        /* silent */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Tick every second
  useEffect(() => {
    if (!settings?.enabled || !settings?.target_iso) return;
    setTime(computeDiff(settings.target_iso));
    const id = setInterval(() => {
      setTime(computeDiff(settings.target_iso));
    }, 1000);
    return () => clearInterval(id);
  }, [settings]);

  if (!settings || !settings.enabled || dismissed || !time) return null;

  const dismiss = () => {
    sessionStorage.setItem(DISMISS_KEY, "1");
    setDismissed(true);
  };

  return (
    <BannerView
      time={time}
      label={settings.label}
      expiredLabel={settings.expired_label}
      onDismiss={dismiss}
    />
  );
};

// Separate component so the body-class side-effect only runs when banner is actually shown
const BannerView = ({ time, label, expiredLabel, onDismiss }) => {
  useEffect(() => {
    document.body.classList.add("has-countdown-banner");
    return () => document.body.classList.remove("has-countdown-banner");
  }, []);

  return (
    <div
      data-testid="countdown-banner"
      className="fixed top-0 left-0 right-0 z-[60] h-10 bg-[#2A1810] text-[#FBF6EA] border-b border-[#A0522D]/60 shadow-[0_4px_12px_-4px_rgba(0,0,0,0.3)]"
    >
      <div className="max-w-7xl h-full mx-auto px-4 sm:px-6 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
          <Clock
            size={14}
            className="text-[#F4C77B] flex-shrink-0 hidden sm:block"
          />
          <p className="text-[10px] sm:text-xs tracking-[0.15em] uppercase text-[#FBF6EA]/90 truncate">
            {time.expired ? expiredLabel : label}
          </p>
        </div>

        {!time.expired && (
          <div
            data-testid="countdown-timer"
            className="flex items-center gap-1.5 sm:gap-3 font-mono text-[11px] sm:text-sm tracking-wider flex-shrink-0"
          >
            <TimeUnit value={time.d} label="g" />
            <span className="text-[#A0522D]">·</span>
            <TimeUnit value={time.h} label="h" />
            <span className="text-[#A0522D]">·</span>
            <TimeUnit value={time.m} label="m" />
            <span className="text-[#A0522D] hidden sm:inline">·</span>
            <TimeUnit value={time.s} label="s" className="hidden sm:flex" />
          </div>
        )}

        <button
          data-testid="countdown-dismiss"
          onClick={onDismiss}
          aria-label="chiudi banner"
          className="text-[#FBF6EA]/60 hover:text-[#F4C77B] transition flex-shrink-0 p-1"
        >
          <X size={14} />
        </button>
      </div>
    </div>
  );
};

const TimeUnit = ({ value, label, className = "" }) => (
  <span className={`flex items-baseline gap-0.5 ${className}`}>
    <strong className="text-[#F4C77B] tabular-nums">
      {String(value).padStart(2, "0")}
    </strong>
    <span className="text-[#FBF6EA]/60 text-[9px] sm:text-[10px]">{label}</span>
  </span>
);
